import { useEffect, useRef, useState } from 'react';

import type { WirePolicyConstraints } from '../Live/liveWireContract';
import * as styles from '../FirstResponder.module.css';
import { LEAD_HELP, WAIT_HELP } from '../Presentation/PilotSettingsSection';
import {
  LiveMode,
  LivePilotPolicy,
  LiveSettingsDraft,
} from '../LivePreview/liveViewModel';
import { useModalA11y } from '../LivePreview/useModalA11y';
import RankingOrderList from './RankingOrderList';

/*
 * The connected pilot-settings editor. Reuses SettingsEditor.tsx's accepted structure and CSS
 * classes verbatim (constraint: connect the accepted design, don't redesign it) but:
 *
 *   - never sees based_on_version / expected_draft_version / Idempotency-Key — those belong to
 *     Connected/index.tsx (Correction 4/9); this component only calls the onSaveDraft(policy,
 *     reason) / onActivate(reason) props it's given and renders whatever SubmitResult comes back;
 *   - never triggers a refetch itself (also index.tsx's job, Correction 5);
 *   - validates against the real server `constraints` object, not a single hardcoded floor;
 *   - adds/removes clients by numeric customer_id only, never a typed name (Correction 7) — the
 *     name shown for an existing client is always the server's; a freshly-added id shows a
 *     pending-confirmation label until the next successful fetch;
 *   - has no SimulatedResponse selector — errors rendered here are real;
 *   - synchronizes its form to the AUTHORITATIVE active/draft policy whenever the version token
 *     it's based on changes (C3) — a genuinely new draft/active row from the server (a successful
 *     save/refetch, or a 409 that brought in someone else's newer draft) replaces the form; an
 *     unrelated rerender with the same token never does;
 *   - never reports the ordinary success notice when the container couldn't confirm fresh state
 *     after a mutation (C4 — `confirmed: false`), and is disabled outright while
 *     `blockedByStaleConfirmation` or `safetyBlocksEdits` is set (Task 8 — configuration is the
 *     only thing a safety failure locks, and it is locked here too).
 */

export type SubmitResult =
  | { ok: true; confirmed?: boolean }
  | {
      ok: false;
      code: 400 | 401 | 403 | 409 | 422 | 'network';
      errors: string[];
      errorCode?: string | null;
    };

const PENDING_CONFIRMATION_LABEL = 'Unknown — pending confirmation';

export const validatePolicy = (
  p: LivePilotPolicy,
  constraints: WirePolicyConstraints,
): string[] => {
  const errors: string[] = [];
  if (p.clients.length < constraints.minimum_clients_for_activation) {
    errors.push(
      `At least ${constraints.minimum_clients_for_activation} pilot client(s) required.`,
    );
  }
  if (p.waitBeforeEvaluationMinutes < constraints.wait_minimum_minutes) {
    errors.push(
      `Wait before evaluation cannot be less than ${constraints.wait_minimum_minutes} minutes.`,
    );
  }
  if (p.minLeadMinutes < constraints.min_lead_floor_minutes) {
    errors.push(
      `Minimum lead time cannot be lowered below the server safety floor of ${constraints.min_lead_floor_minutes} minutes.`,
    );
  }
  if (p.maxOffersPerJob < constraints.max_offers_minimum) {
    errors.push(
      `Max offers per job must be at least ${constraints.max_offers_minimum}.`,
    );
  }
  if (p.distanceLimitMiles < constraints.distance_limit_minimum_miles) {
    errors.push(
      `Distance limit must be at least ${constraints.distance_limit_minimum_miles} mile(s).`,
    );
  }
  if (
    p.treatmentPercent < constraints.treatment_percent_min ||
    p.treatmentPercent > constraints.treatment_percent_max
  ) {
    errors.push(
      `Experiment allocation must be between ${constraints.treatment_percent_min} and ${constraints.treatment_percent_max} percent.`,
    );
  }
  return errors;
};

type Props = {
  active: LivePilotPolicy;
  draft: LiveSettingsDraft | null;
  constraints: WirePolicyConstraints;
  // GRAVY-10373 C1 follow-up: the caller passes REQUESTED mode, not effective mode — the
  // backend's PolicyActivation judges "is First Responder off" against the configured intent, so
  // a gate-halted SHADOW (requested shadow, effective off) must still block activation exactly
  // like a genuinely running SHADOW would.
  mode: LiveMode;
  // C4: the container couldn't confirm fresh state after the last mutation (a successful
  // mutation whose follow-up refetch failed, or a 409 whose follow-up refetch also failed) —
  // disable submission entirely until a Reload succeeds. Never computed from anything this
  // component fetches itself.
  blockedByStaleConfirmation: boolean;
  // GRAVY-10373 Task 8: safety is not clear, so no configuration change may be submitted. The
  // container closes this editor outright when that happens — this is the innermost layer of the
  // same rule, so the component is safe wherever it is rendered from.
  safetyBlocksEdits?: boolean;
  onSaveDraft: (
    policy: LivePilotPolicy,
    reason: string,
  ) => Promise<SubmitResult>;
  onActivate: (reason: string) => Promise<SubmitResult>;
  onClose: () => void;
};

const num = (v: string, fallback: number): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

const cloneOf = (
  draft: LiveSettingsDraft | null,
  active: LivePilotPolicy,
): LivePilotPolicy => JSON.parse(JSON.stringify(draft ? draft.policy : active));

// C3: the token this component's CURRENT form is based on — a draft's own version if one exists,
// else the active policy's. A change here (not a change in object identity) is what "genuinely
// new authoritative data" means; anything else is an unrelated rerender.
const authoritativeVersionOf = (
  draft: LiveSettingsDraft | null,
  active: LivePilotPolicy,
): string => (draft ? draft.policy.version : active.version);

const ConnectedPolicyEditor = ({
  active,
  draft,
  constraints,
  mode,
  blockedByStaleConfirmation,
  safetyBlocksEdits = false,
  onSaveDraft,
  onActivate,
  onClose,
}: Props) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  useModalA11y(true, onClose, dialogRef);

  const [form, setForm] = useState<LivePilotPolicy>(() =>
    cloneOf(draft, active),
  );
  const [syncedVersion, setSyncedVersion] = useState(() =>
    authoritativeVersionOf(draft, active),
  );
  const [reason, setReason] = useState('');
  const [pending, setPending] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [httpCode, setHttpCode] = useState<number | 'network' | null>(null);
  const [backendErrorCode, setBackendErrorCode] = useState<string | null>(null);
  const [savedNotice, setSavedNotice] = useState<string | null>(null);
  const [unconfirmedNotice, setUnconfirmedNotice] = useState<string | null>(
    null,
  );
  const [activateConfirm, setActivateConfirm] = useState(false);
  const [newClientId, setNewClientId] = useState('');

  // C3: reset the form ONLY when the authoritative version token actually changes — never on an
  // unrelated rerender that leaves it the same.
  const authoritativeVersion = authoritativeVersionOf(draft, active);
  useEffect(() => {
    if (authoritativeVersion !== syncedVersion) {
      setForm(cloneOf(draft, active));
      setSyncedVersion(authoritativeVersion);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authoritativeVersion, syncedVersion]);

  const set = <K extends keyof LivePilotPolicy>(
    key: K,
    value: LivePilotPolicy[K],
  ) => setForm((f) => ({ ...f, [key]: value }));

  const showResult = (r: SubmitResult, successNotice: string) => {
    if (r.ok) {
      setErrors([]);
      setHttpCode(null);
      setBackendErrorCode(null);
      if (r.confirmed === false) {
        setSavedNotice(null);
        setUnconfirmedNotice(
          'The change may have saved, but the latest server state could not be confirmed. ' +
            'Reload before making another change.',
        );
      } else {
        setUnconfirmedNotice(null);
        setSavedNotice(successNotice);
      }
    } else {
      setSavedNotice(null);
      setUnconfirmedNotice(null);
      setErrors(r.errors);
      setHttpCode(r.code);
      setBackendErrorCode(r.errorCode ?? null);
    }
  };

  const activationBlocked = mode !== 'off';
  const submissionBlocked = blockedByStaleConfirmation;
  // Two independent reasons, each with its own notice below; either one blocks every submit.
  const editsBlocked = submissionBlocked || safetyBlocksEdits;

  return (
    <div
      className={styles.overlay}
      data-testid="flp-settings-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="flp-settings-title"
        ref={dialogRef}
        tabIndex={-1}
        data-testid="flp-settings-editor"
        style={{ maxWidth: 720 }}
      >
        <div className={styles.modalHeader}>
          <div>
            <h3 id="flp-settings-title">Edit pilot settings</h3>
            <div
              className={`${styles.muted} ${styles.small}`}
              style={{ marginTop: 4 }}
            >
              Active version <b>{active.version}</b> is immutable — saving
              creates a draft; activation is a separate, audited step. Changes
              never rewrite recorded runs.
            </div>
          </div>
          <button
            type="button"
            className={styles.x}
            aria-label="Close"
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <div className={styles.section}>
          <div
            className={`${styles.muted} ${styles.small}`}
            data-testid="flp-settings-version"
          >
            {draft ? (
              <>
                Editing <b>draft</b> based on {draft.basedOnVersion}.
              </>
            ) : (
              <>
                No draft yet — saving will create one based on {active.version}.
              </>
            )}
          </div>

          {/* Clients — numeric customer_id only, server-resolved names only */}
          <div style={{ marginTop: 16 }}>
            <div className={styles.k}>Pilot clients</div>
            <div className={styles.filters} style={{ marginTop: 8 }}>
              {form.clients.map((c) => (
                <span
                  key={c.id}
                  className={styles.chip}
                  data-testid={`flp-client-${c.id}`}
                >
                  {c.name} <span className={styles.chipCnt}>#{c.id}</span>
                  <button
                    type="button"
                    className={styles.btnPlain}
                    aria-label={`Remove ${c.name}`}
                    style={{ marginLeft: 6 }}
                    onClick={() =>
                      set(
                        'clients',
                        form.clients.filter((x) => x.id !== c.id),
                      )
                    }
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
            <div
              style={{
                display: 'flex',
                gap: 8,
                marginTop: 8,
                flexWrap: 'wrap',
                alignItems: 'center',
              }}
            >
              <input
                aria-label="New client ID"
                placeholder="Client ID"
                inputMode="numeric"
                value={newClientId}
                onChange={(e) => setNewClientId(e.target.value)}
                className={styles.input}
                style={{ width: 110 }}
              />
              <button
                type="button"
                className={`${styles.btn} ${styles.btnOutline} ${styles.btnSm}`}
                data-testid="flp-add-client"
                onClick={() => {
                  const id = Number(newClientId);
                  if (!Number.isInteger(id) || id <= 0) return;
                  if (form.clients.some((c) => c.id === id)) return;
                  set('clients', [
                    ...form.clients,
                    { id, name: PENDING_CONFIRMATION_LABEL },
                  ]);
                  setNewClientId('');
                }}
              >
                Add client
              </button>
            </div>
            <div
              className={`${styles.muted} ${styles.small}`}
              style={{ marginTop: 6 }}
              data-testid="flp-client-id-note"
            >
              Internal/QA-only limitation, not final UX: clients are added by
              numeric ID only. The server-confirmed name appears after the next
              successful save or reload.
            </div>
          </div>

          {/* Numeric policy fields — floors sourced from the real constraints object */}
          <div
            className={`${styles.sig} ${styles.sigFlush}`}
            style={{ marginTop: 20 }}
          >
            <div>
              <div className={styles.k}>Wait before evaluation (min)</div>
              <input
                aria-label="Wait before evaluation minutes"
                inputMode="numeric"
                className={styles.input}
                value={String(form.waitBeforeEvaluationMinutes)}
                onChange={(e) =>
                  set(
                    'waitBeforeEvaluationMinutes',
                    num(e.target.value, form.waitBeforeEvaluationMinutes),
                  )
                }
              />
              <div
                className={`${styles.muted} ${styles.small}`}
                style={{ marginTop: 4 }}
              >
                {WAIT_HELP} 0 = no additional wait.
              </div>
            </div>
            <div>
              <div className={styles.k}>Minimum lead time (min)</div>
              <input
                aria-label="Minimum lead time minutes"
                inputMode="numeric"
                className={styles.input}
                value={String(form.minLeadMinutes)}
                onChange={(e) =>
                  set(
                    'minLeadMinutes',
                    num(e.target.value, form.minLeadMinutes),
                  )
                }
              />
              <div
                className={`${styles.muted} ${styles.small}`}
                style={{ marginTop: 4 }}
              >
                {LEAD_HELP} Server safety floor{' '}
                {constraints.min_lead_floor_minutes} min: can be raised, never
                lowered.
              </div>
            </div>
            <div>
              <div className={styles.k}>Max offers per job</div>
              <input
                aria-label="Max offers per job"
                inputMode="numeric"
                className={styles.input}
                value={String(form.maxOffersPerJob)}
                onChange={(e) =>
                  set(
                    'maxOffersPerJob',
                    num(e.target.value, form.maxOffersPerJob),
                  )
                }
              />
            </div>
            <div>
              <div className={styles.k}>Distance limit (miles)</div>
              <input
                aria-label="Distance limit miles"
                inputMode="numeric"
                className={styles.input}
                value={String(form.distanceLimitMiles)}
                onChange={(e) =>
                  set(
                    'distanceLimitMiles',
                    num(e.target.value, form.distanceLimitMiles),
                  )
                }
              />
            </div>
            <div>
              <div className={styles.k}>
                Experiment allocation (% Treatment)
              </div>
              <input
                aria-label="Experiment treatment percent"
                inputMode="numeric"
                className={styles.input}
                value={String(form.treatmentPercent)}
                onChange={(e) =>
                  set(
                    'treatmentPercent',
                    num(e.target.value, form.treatmentPercent),
                  )
                }
              />
              <div
                className={`${styles.muted} ${styles.small}`}
                style={{ marginTop: 4 }}
              >
                Deterministic JOB-level assignment, persisted with the
                experiment version. Both arms evaluate and record a comparable
                shortlist; control never creates offers or contacts workers.
              </div>
            </div>
          </div>

          {/* Ranking order — frozen, unchanged (constraint #8) */}
          <div style={{ marginTop: 20 }}>
            <div className={styles.k}>Ranking order</div>
            <RankingOrderList />
          </div>

          {/* Reason + actions — no simulated-response control; errors here are real */}
          <div style={{ marginTop: 16 }}>
            <div className={styles.k}>Reason (required)</div>
            <input
              aria-label="Change reason"
              placeholder="Why is this changing?"
              className={styles.input}
              style={{ width: '100%', marginTop: 6 }}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>

          {errors.length > 0 && (
            <div
              className={styles.formErrors}
              role="alert"
              data-testid="flp-settings-errors"
            >
              {httpCode !== null && (
                <b>
                  Error {httpCode}
                  {backendErrorCode ? ` (${backendErrorCode})` : ''}.{' '}
                </b>
              )}
              <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                {errors.map((e) => (
                  <li key={e}>{e}</li>
                ))}
              </ul>
            </div>
          )}
          {savedNotice && (
            <div
              className={`${styles.muted} ${styles.small}`}
              role="status"
              data-testid="flp-settings-saved"
            >
              {savedNotice}
            </div>
          )}
          {unconfirmedNotice && (
            <div
              className={styles.formErrors}
              role="alert"
              data-testid="flp-settings-unconfirmed"
            >
              {unconfirmedNotice}
            </div>
          )}
          {submissionBlocked && (
            <div
              className={`${styles.muted} ${styles.small}`}
              style={{ marginTop: 8 }}
              data-testid="flp-submission-blocked"
            >
              Reload the console before making another change — the last
              confirmation could not be verified.
            </div>
          )}
          {safetyBlocksEdits && (
            <div
              className={`${styles.muted} ${styles.small}`}
              style={{ marginTop: 8 }}
              data-testid="flp-safety-blocked"
            >
              Configuration changes are disabled until the safety status is
              clear.
            </div>
          )}

          <div
            style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}
          >
            <button
              type="button"
              className={`${styles.btn} ${styles.btnOutline}`}
              data-testid="flp-save-draft"
              disabled={pending || editsBlocked}
              onClick={async () => {
                if (!reason.trim()) {
                  setSavedNotice(null);
                  setHttpCode(422);
                  setBackendErrorCode(null);
                  setErrors(['A reason is required to save the draft.']);
                  return;
                }
                const local = validatePolicy(form, constraints);
                if (local.length > 0) {
                  setSavedNotice(null);
                  setHttpCode(422);
                  setBackendErrorCode(null);
                  setErrors(local);
                  return;
                }
                setPending(true);
                try {
                  const result = await onSaveDraft(form, reason.trim());
                  showResult(
                    result,
                    'Draft saved. Activation is a separate step.',
                  );
                } finally {
                  setPending(false);
                }
              }}
            >
              {pending ? 'Saving…' : 'Save draft'}
            </button>
            {!activateConfirm ? (
              <button
                type="button"
                className={`${styles.btn} ${styles.btnDanger}`}
                data-testid="flp-activate"
                disabled={
                  !draft || activationBlocked || pending || editsBlocked
                }
                title={
                  activationBlocked
                    ? 'Activation is allowed only while First Responder is OFF'
                    : undefined
                }
                onClick={() => setActivateConfirm(true)}
              >
                Activate draft…
              </button>
            ) : (
              <span
                style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}
              >
                <span className={`${styles.muted} ${styles.small}`}>
                  Activate the draft as the new immutable version?
                </span>
                <button
                  type="button"
                  className={`${styles.btn} ${styles.btnDanger}`}
                  data-testid="flp-activate-confirm"
                  disabled={pending || editsBlocked}
                  onClick={async () => {
                    setActivateConfirm(false);
                    if (!reason.trim()) {
                      setSavedNotice(null);
                      setHttpCode(422);
                      setBackendErrorCode(null);
                      setErrors([
                        'A reason is required to activate the draft.',
                      ]);
                      return;
                    }
                    setPending(true);
                    try {
                      const result = await onActivate(reason.trim());
                      showResult(
                        result,
                        'Draft activated. Audit rows recorded; recorded runs unchanged.',
                      );
                    } finally {
                      setPending(false);
                    }
                  }}
                >
                  Confirm activation
                </button>
                <button
                  type="button"
                  className={`${styles.btn} ${styles.btnOutline}`}
                  onClick={() => setActivateConfirm(false)}
                >
                  Cancel
                </button>
              </span>
            )}
            <button
              type="button"
              className={`${styles.btn} ${styles.btnOutline}`}
              onClick={onClose}
            >
              Done
            </button>
          </div>
          {activationBlocked && (
            <div
              className={`${styles.muted} ${styles.small}`}
              style={{ marginTop: 8 }}
              data-testid="flp-activate-blocked"
            >
              Activation is allowed only while First Responder is OFF.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConnectedPolicyEditor;
